import { ReactNode, useEffect, useRef } from 'react'

import { Box, CircularProgress, Table, TableBody, TableContainer, useTheme } from '@mui/material'
import { keyframes } from '@mui/material'

import { Scrollbar } from '../Scrollbar'
import { NoDataPlaceholder } from '../table/base/TableBase/components/NoDataPlaceholder'
import { Row } from './components/Row'
import { HorizontalTableProps } from './HorizontalTable.types'
import { HorizontalTableSkeleton } from './HorizontalTableSkeleton'
export const HORIZONTAL_TABLE_TEST_ID = 'horizontal-table' as const
export const HORIZONTAL_TABLE_NO_DATA_MESSAGE = 'No data available' as const

const highlightColumn = keyframes`
  0% { background-color: none; }
  50% { background-color: rgba(255, 89, 17, 0.3); }
  100% { background-color: none; }
`

export const HorizontalTable = <Data extends Record<string, unknown>>({
  data: columns,
  rows,
  shouldHighlightColumn,
  testId,
  loading,
  fetching,
  noDataMessage = HORIZONTAL_TABLE_NO_DATA_MESSAGE
}: HorizontalTableProps<Data>) => {
  const scrollRef = useRef<HTMLDivElement | undefined>(undefined)
  const theme = useTheme()

  useEffect(() => {
    if (!scrollRef.current) return

    const timeout = setTimeout(() => {
      scrollRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      })
    }, 300)

    return () => clearTimeout(timeout)
  }, [scrollRef.current])

  const shouldDisplayNoData = !loading && columns.length === 0
  const shouldDisplayData = !loading && columns.length > 0

  if (loading) {
    return <HorizontalTableSkeleton columns={6} rows={4} />
  }

  return (
    <Box
      data-testid={testId || HORIZONTAL_TABLE_TEST_ID}
      m={2}
      pb={-1}
      mb={1}
      sx={{
        borderTopLeftRadius: Number(theme.shape.borderRadius) * 2
      }}
    >
      <Scrollbar
        style={{
          borderTopLeftRadius: Number(theme.shape.borderRadius) * 2
        }}
      >
        {fetching && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            mb={1.5}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor={({ palette: { mode } }) =>
              mode === 'light' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.2)'
            }
            zIndex={1}
            sx={{
              borderBottomLeftRadius: Number(theme.shape.borderRadius) * 2
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <TableContainer>
          <Table
            sx={{
              mb: 1.5,
              borderCollapse: 'unset',

              '& tr:first-of-type td': {
                borderTop: `1px solid ${theme.palette.horizontalTable.border.color}`
              },
              '& tr:first-of-type td:last-child': {
                borderTopRightRadius: Number(theme.shape.borderRadius) * 2
              },
              '& tr:last-child td': {
                borderBottom: `1px solid ${theme.palette.horizontalTable.border.color}`
              },
              '& tr:last-child td:first-of-type': {
                boxShadow: `-1px 10px ${theme.palette.mode === 'light' ? '#FFFFFF' : '#424242'}`,
                borderBottomLeftRadius: Number(theme.shape.borderRadius) * 2
              },
              '& tr:last-child td:last-child': {
                borderBottomRightRadius: Number(theme.shape.borderRadius) * 2
              },

              '& tr td': {
                borderRight: `1px solid ${theme.palette.horizontalTable.border.color}`
              },
              ...(shouldDisplayNoData
                ? {
                    td: {
                      border: `1px solid ${theme.palette.horizontalTable.border.color}`,
                      borderTopLeftRadius: Number(theme.shape.borderRadius) * 2
                    }
                  }
                : {})
            }}
          >
            <TableBody>
              {shouldDisplayNoData && <NoDataPlaceholder colSpan={1} message={noDataMessage} />}

              {shouldDisplayData &&
                rows.map((row, rowIndex) => {
                  return (
                    <Row
                      sx={row.sx}
                      key={row.id}
                      header={row.label}
                      values={columns.map((column) => (
                        <Box
                          ref={
                            rowIndex === 0 && shouldHighlightColumn?.(column)
                              ? scrollRef
                              : undefined
                          }
                          m={-2}
                          p={2}
                          sx={{
                            animation: shouldHighlightColumn?.(column)
                              ? `${highlightColumn} 1s ease 3`
                              : 'none'
                          }}
                        >
                          {row.transform ? row.transform(column) : (column[row.id] as ReactNode)}
                        </Box>
                      ))}
                    />
                  )
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>
    </Box>
  )
}
